import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { cardRoutes, useCreateCard } from 'entities/card';
import { getFirstValidationError, isAxiosResponseError, isValidationErrorsModel, api } from 'shared/lib/api';
import { showErrorToast } from 'shared/lib/notifications';
import { logError } from 'shared/lib/log-errors';

import { getInputErrorByCode } from './errors-map';

const DEFAULT_STRIPE_ERROR_TEXT = 'The service is unavailable';

export function useAddCard() {
  const createCardQuery = useCreateCard();
  const methods = useFormContext();
  const elements = useElements();
  const stripe = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  function setError(field: string, message: string) {
    methods.setError(field, {
      type: 'manual',
      message,
    });
  }

  return {
    isSuccess,
    isLoading,
    submit: async () => {
      const card = elements?.getElement(CardNumberElement);

      if (!stripe || !card) {
        logError(DEFAULT_STRIPE_ERROR_TEXT);
        showErrorToast(DEFAULT_STRIPE_ERROR_TEXT);

        return;
      }

      const { line1, line2, city, state, country, name, postal_code } = methods.watch();

      setIsLoading(true);

      try {
        const response = await api.post<{ data: { setupIntent: string } }>(cardRoutes.app.createSetupIntent, {});

        const { setupIntent, error } = await stripe.confirmCardSetup(response.data.data.setupIntent, {
          payment_method: {
            card,
            billing_details: {
              address: { line1, line2, city, state, country, postal_code },
              name,
            },
          },
        });

        if (error?.code) {
          logError(error);
          setIsLoading(false);

          const errorObj = getInputErrorByCode(error.code);

          if (errorObj) {
            setError(errorObj.name, errorObj.error);
          }
        }

        if (setupIntent && setupIntent.status === 'succeeded') {
          try {
            await createCardQuery.mutateAsync({
              payload: {
                tokenId: setupIntent.payment_method,
              },
            });
            setIsSuccess(true);
          } catch (e) {
            logError(e);

            setIsLoading(false);

            let errorText = DEFAULT_STRIPE_ERROR_TEXT;

            if (isAxiosResponseError(e) && isValidationErrorsModel(e.response.data)) {
              errorText = getFirstValidationError(e.response.data.errors).message;
            }

            showErrorToast(errorText);
          } finally {
            setIsLoading(false);
          }
        }
      } catch (e) {
        logError(e);
      }
    },
  };
}
